/**
 * GE Lines Pte Ltd — Self-Service Customer Portal
 *
 * Architecture mirrors v1 (apps/frontends/self-service) but runs inside
 * the v2 booxplatform2026 monorepo with Tailwind v4 + react-router-dom v7.
 *
 * Route map:
 *   Public  → /  /track  /track/:ref  /contact  /login
 *   Portal  → /portal  /portal/trucking  /portal/depot
 *              /portal/warehouse  /portal/bills-payments
 *              /portal/documents-reports  /portal/users
 */

import { useEffect, type ReactNode } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from '@/context/AuthContext';
import { RequireAuth } from '@/components/RequireAuth';
import type { PortalPermission } from '@/lib/portal-access';
import { PublicLayout } from '@/layouts/PublicLayout';
import { PortalLayout } from '@/layouts/PortalLayout';
import { useAuth } from '@/context/AuthContext';
import { useStorefront } from '@/context/StorefrontContext';
import { SurfaceAccessProvider } from '@/context/SurfaceAccessContext';

// Public pages
import { HomePage } from '@/pages/public/HomePage';
import { TrackPage } from '@/pages/public/TrackPage';
import { TrackResultPage } from '@/pages/public/TrackResultPage';
import { ContactPage } from '@/pages/public/ContactPage';
import { LoginPage } from '@/pages/public/LoginPage';
import { SailingSchedulePage } from '@/pages/public/SailingSchedulePage';
import { StorefrontRuntimeRoutePage } from '@/pages/public/StorefrontRuntimeRoutePage';
import { SurfaceRequestPage } from '@/pages/public/SurfaceRequestPage';
import { DepotPortalPage } from '@/pages/public/DepotPortalPage';

// Components
import { AiChatWidget } from '@/components/AiChatWidget';

import { ShipmentDetailPage } from '@/pages/portal/ShipmentDetailPage';
import { DynamicRuntimePortalPage } from '@/pages/portal/DynamicRuntimePortalPage';
import {
  BillsPaymentsPage,
  CustomerUsersPage,
  DepotCustomerPage,
  DocumentsReportsPage,
  OverviewPage,
  TruckingPage,
  WarehouseCustomerPage,
} from '@/pages/portal/ServicePortalPages';

function RootEntryPage() {
  const { isAuthenticated, isLoading } = useAuth();
  const { experienceMode } = useStorefront();

  if (experienceMode === 'portal') {
    if (isLoading) return null;
    return <Navigate to={isAuthenticated ? '/portal' : '/login'} replace />;
  }

  return <HomePage />;
}

function DisabledPortalRedirect() {
  const { storefrontBaseUrl } = useStorefront();
  useEffect(() => {
    if (storefrontBaseUrl?.startsWith('http')) {
      window.location.replace(storefrontBaseUrl);
    }
  }, [storefrontBaseUrl]);
  if (storefrontBaseUrl?.startsWith('http')) return null;
  return <Navigate to={storefrontBaseUrl ?? '/'} replace />;
}

function LoginRoutePage() {
  const { featureFlags } = useStorefront();
  if (!featureFlags.portalLogin) return <DisabledPortalRedirect />;
  return <LoginPage />;
}

function PortalRoutes() {
  const { featureFlags } = useStorefront();
  if (!featureFlags.portalLogin) return <DisabledPortalRedirect />;

  return (
    <RequireAuth>
      <PortalLayout />
    </RequireAuth>
  );
}

function PortalPageGate({ permission, children }: { permission: PortalPermission; children: ReactNode }) {
  return <RequireAuth permission={permission}>{children}</RequireAuth>;
}

function FeatureRedirect() {
  return <Navigate to="/" replace />;
}

function TrackingRoutePage() {
  const { featureFlags } = useStorefront();
  if (featureFlags.tracking === false) return <FeatureRedirect />;
  return <TrackPage />;
}

function TrackResultRoutePage() {
  const { featureFlags } = useStorefront();
  if (featureFlags.tracking === false) return <FeatureRedirect />;
  return <TrackResultPage />;
}

function BookingRoutePage() {
  const { featureFlags } = useStorefront();
  if (featureFlags.bookings === false) return <FeatureRedirect />;
  return <SurfaceRequestPage requestType="booking" />;
}

function RfqRoutePage() {
  const { featureFlags } = useStorefront();
  if (featureFlags.rates === false) return <FeatureRedirect />;
  return <SurfaceRequestPage requestType="rfq" />;
}

function InquiryRoutePage() {
  const { featureFlags } = useStorefront();
  if (featureFlags.otherInquiry === false) return <FeatureRedirect />;
  return <SurfaceRequestPage requestType="inquiry" />;
}

function ScheduleRoutePage() {
  const { featureFlags } = useStorefront();
  if (featureFlags.sailingSchedule === false) return <FeatureRedirect />;
  return <SailingSchedulePage />;
}

function ContactRoutePage() {
  const { routes } = useStorefront();
  if (!routes.some((route) => route.routePath === '/contact')) return <FeatureRedirect />;
  return <ContactPage />;
}

export function App() {
  const { featureFlags } = useStorefront();

  return (
    <AuthProvider>
      <SurfaceAccessProvider>
        {featureFlags.aiChat !== false && <AiChatWidget />}
        <Routes>
          {/* ── Public routes ──────────────────────────────────── */}
          <Route element={<PublicLayout />}>
            <Route path="/" element={<RootEntryPage />} />
            <Route path="/track" element={<TrackingRoutePage />} />
            <Route path="/track/:reference" element={<TrackResultRoutePage />} />
            <Route path="/book" element={<BookingRoutePage />} />
            <Route path="/rfq" element={<RfqRoutePage />} />
            <Route path="/inquiry" element={<InquiryRoutePage />} />
            <Route path="/schedule" element={<ScheduleRoutePage />} />
            <Route path="/contact" element={<ContactRoutePage />} />
            <Route path="/login" element={<LoginRoutePage />} />
            <Route path="/depot-portal" element={<DepotPortalPage />} />
            <Route path="*" element={<StorefrontRuntimeRoutePage />} />
          </Route>

          {/* ── Portal routes (auth required) ──────────────────── */}
          <Route element={<PortalRoutes />}>
            <Route path="/portal" element={<OverviewPage />} />
            <Route path="/portal/trucking" element={<PortalPageGate permission="portal:operations:read"><TruckingPage /></PortalPageGate>} />
            <Route path="/portal/depot" element={<PortalPageGate permission="portal:operations:read"><DepotCustomerPage /></PortalPageGate>} />
            <Route path="/portal/warehouse" element={<PortalPageGate permission="portal:operations:read"><WarehouseCustomerPage /></PortalPageGate>} />
            <Route path="/portal/bills-payments" element={<PortalPageGate permission="portal:finance:read"><BillsPaymentsPage /></PortalPageGate>} />
            <Route path="/portal/documents-reports" element={<PortalPageGate permission="portal:documents:read"><DocumentsReportsPage /></PortalPageGate>} />
            <Route path="/portal/users" element={<PortalPageGate permission="portal:users:manage"><CustomerUsersPage /></PortalPageGate>} />
            <Route path="/portal/shipments/:id" element={<PortalPageGate permission="portal:operations:read"><ShipmentDetailPage /></PortalPageGate>} />
            <Route path="/portal/shipments" element={<Navigate to="/portal/trucking" replace />} />
            <Route path="/portal/invoices" element={<Navigate to="/portal/bills-payments" replace />} />
            <Route path="/portal/bookings" element={<Navigate to="/portal/trucking" replace />} />
            <Route path="/portal/documents" element={<Navigate to="/portal/documents-reports" replace />} />
            <Route path="/portal/settings" element={<Navigate to="/portal/users" replace />} />
            <Route path="/portal/:moduleCode" element={<PortalPageGate permission="portal:operations:read"><DynamicRuntimePortalPage /></PortalPageGate>} />
          </Route>
        </Routes>
      </SurfaceAccessProvider>
    </AuthProvider>
  );
}
